import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormAttachmentUploadRequirements } from '../attachments/form-attachment-requirements';
import { GeneralFormData } from '../general-form-data.model';
import { formatTimeForDisplay, parseLocalDate } from '../../../utils/date-time-formatter';
import { FormStep } from '../form-steps.interface';


export class SupervisorInjuryReport {
    //Form Metadata
    id?: string;
    submittedByName: string = '';
    submittedByEmail: string = '';
    submissionDate?: Date | null;
 

    //Employee Information
    employeeFirstName: string = '';
    employeeLastName: string = '';
    streetAddress: string = '';
    city: string = '';
    stateProvince: string = '';
    zipPostalCode!: number | null;
    phone: string = '';
    dateOfHire: Date | null = null;
    craftAndPosition: string = '';

    //Job Information
    jobName: string = '';
    shiftWorked: string = '';
    supervisorFirstName: string = '';
    supervisorLastName: string = '';
    hoursWorkedPerDay!: number | null;
    hoursWorkedPerWeek!: number | null;

    //Incident Information
    typeOfIncident: string = '';
    dateOfIncident: Date | null = null;
    timeOfIncident: string = '';
    dateReported: Date | null = null;
    timeReported: string = '';
    incidentLocation: string = '';
    weather: string = '';
    temperature!: number | null;
    equipment1: string = '';
    equipment2: string = '';
    anyDefects: string = '';
    coveredInPreTask: string = '';
    witness1: string = '';
    witness2: string = '';
    employeeActivity: string = '';
    contributingFactors: string = '';
    safetyProcedures: string = '';
    employeeFamiliarWithProcedures: string = '';
    employeeFollowedProcedures: string = '';
    whyNotFollowed: string = '';
    // Incident Causes - Individual boolean properties
    awkwardBodyPositioning: boolean = false;
    causedByThirdParty: boolean = false;
    complacency: boolean = false;
    incorrectTool: boolean = false;
    materialFailure: boolean = false;
    equipmentToolFailure: boolean = false;
    failureToMeetTargets: boolean = false;
    failureToUsePpe: boolean = false;
    lackOfFallProtection: boolean = false;
    improperLiftingTechnique: boolean = false;
    improperLadderUsage: boolean = false;
    ignoringSafeWorkProcedures: boolean = false;
    lackOfKnowledgeTraining: boolean = false;
    notCommunicating: boolean = false;
    numerousMinorInjuries: boolean = false;
    occupationalIllness: boolean = false;
    physicalViolence: boolean = false;
    poorHousekeeping: boolean = false;
    poorVisibilityLighting: boolean = false;
    preoccupied: boolean = false;
    slippingTripping: boolean = false;
    supervisorManagementFailure: boolean = false;
    unresolvedIssues: boolean = false;
    willfulRuleViolation: boolean = false;
    otherCause: boolean = false;
    otherCauseSpecify: string = '';
    incidentCausesDetail: string = '';

    //Injury Information
    injured: string = '';
    bloodExposure: string = '';
    seekMedicalTreatment: string = '';
    treatmentFacilityName: string = '';
    doctorFirstName: string = '';
    doctorLastName: string = '';
    doctorContactInfo: string = '';
    propertyDamage: string = '';
    othersInvolved: string = '';
    preventionSuggestion: string = '';
    picturesDiagrams: Array<string> = [];
    signature: Array<string> = [];
    signatureDate: Date | null = null;

    constructor(data?: Partial<SupervisorInjuryReport>) {
        // Set default date to today if not provided
   
        
        if (data) {
            Object.assign(this, data);
            //convert dates
            this.signatureDate = data.signatureDate ? parseLocalDate(data.signatureDate as any) : null;
            this.submissionDate = data.submissionDate ? new Date(data.submissionDate) : null;

            this.dateOfHire = data.dateOfHire ? parseLocalDate(data.dateOfHire as any) : null;
            this.dateOfIncident = data.dateOfIncident ? parseLocalDate(data.dateOfIncident as any) : null;
            this.dateReported = data.dateReported ? parseLocalDate(data.dateReported as any) : null;

            //convert time
            this.timeOfIncident = data.timeOfIncident ? formatTimeForDisplay(data.timeOfIncident) : '';
            this.timeReported = data.timeReported ? formatTimeForDisplay(data.timeReported) : '';
            //convert strings to numbers
            this.zipPostalCode = data.zipPostalCode ? Number(data.zipPostalCode) : null;
            this.hoursWorkedPerDay = data.hoursWorkedPerDay ? Number(data.hoursWorkedPerDay) : 0;
            this.hoursWorkedPerWeek = data.hoursWorkedPerWeek ? Number(data.hoursWorkedPerWeek) : 0;
            this.temperature = data.temperature ? Number(data.temperature) : 0;

       
        }
    }

    static getFormSteps(): FormStep[] {
        return [
            {
                id: 'employee-details',
                shortTitle: 'Employee Details',
                title: 'Employee Details',
                icon: 'person-outline',
                description: 'Employee information and contact details',
                sections: [
                    {
                        fields: ['employeeFirstName', 'employeeLastName', 'streetAddress', 'city', 'stateProvince', 'zipPostalCode', 'phone', 'dateOfHire', 'craftAndPosition']
                    }
                ]
            },
            {
                id: 'job-details',
                shortTitle: 'Job Details',
                title: 'Job Details',
                icon: 'business-outline',
                description: 'Job information and supervisor details',
                sections: [
                    {
                        fields: ['jobName', 'shiftWorked', 'hoursWorkedPerDay', 'hoursWorkedPerWeek', 'supervisorFirstName', 'supervisorLastName']
                    }
                ]
            },
            {
                id: 'incident-details',
                shortTitle: 'Incident Details',
                title: 'Incident Details',
                icon: 'alert-circle-outline',
                description: 'Incident timing, location, and circumstances',
                sections: [
                    {
                        fields: ['typeOfIncident', 'dateOfIncident', 'timeOfIncident', 'dateReported', 'timeReported', 'incidentLocation', 'weather', 'temperature']
                    },
                    {
                        title: 'Equipment',
                        fields: ['equipment1', 'equipment2', 'anyDefects', 'coveredInPreTask']
                    },
                    {
                        title: 'Witnesses',
                        fields: ['witness1', 'witness2']
                    }
                ]
            },
            {
                id: 'incident-analysis',
                shortTitle: 'Incident Analysis',
                title: 'Incident Analysis',
                icon: 'analytics-outline',
                description: 'Analysis of incident causes and factors',
                sections: [
                    {
                        fields: ['employeeActivity', 'contributingFactors', 'safetyProcedures', 'employeeFamiliarWithProcedures', 'employeeFollowedProcedures', 'whyNotFollowed']
                    },
                    {
                        title: 'Incident Causes',
                        checkboxGroups: [
                            {
                                fields: ['awkwardBodyPositioning', 'causedByThirdParty', 'complacency', 'incorrectTool', 'materialFailure', 'equipmentToolFailure', 'failureToMeetTargets', 'failureToUsePpe', 'lackOfFallProtection', 'improperLiftingTechnique', 'improperLadderUsage', 'ignoringSafeWorkProcedures', 'lackOfKnowledgeTraining', 'notCommunicating', 'numerousMinorInjuries', 'occupationalIllness', 'physicalViolence', 'poorHousekeeping', 'poorVisibilityLighting', 'preoccupied', 'slippingTripping', 'supervisorManagementFailure', 'unresolvedIssues', 'willfulRuleViolation', 'otherCause'],
                                checkboxLabel: 'What Cause the Incident? (Check all that Apply)'
                            }
                        ],
                        fields: ['otherCauseSpecify', 'incidentCausesDetail']
                    }
                ]
            },
            {
                id: 'injury-assessment',
                shortTitle: 'Injury Assessment',
                title: 'Injury Assessment',
                icon: 'medkit-outline',
                description: 'Assessment of injuries and medical treatment',
                sections: [
                    {
                        fields: ['injured', 'bloodExposure', 'seekMedicalTreatment']
                    },
                    {
                        title: 'Medical Treatment Details',
                        fields: ['treatmentFacilityName', 'doctorFirstName', 'doctorLastName', 'doctorContactInfo']
                    },
                    {
                        fields: ['propertyDamage', 'othersInvolved', 'preventionSuggestion']
                    },
                    {
                        title: 'Photos/Diagrams',
                        fields: ['picturesDiagrams']
                    }
                ]
            },
            {
                id: 'sign-off',
                shortTitle: 'Sign Off',
                title: 'Sign Off',
                icon: 'pencil-outline',
                description: 'Sign off and completion',
                sections: [
                    {
                        fields: ['signatureDate', 'signature']
                    }
                ]
            }
        ];
    }

    static getFormInfo(): GeneralFormData {
        return {
            formType: 'supervisor-injury-report',
            formTitle: 'Supervisor Injury Report',
            formIcon: 'medkit-outline',
            cardImage: 'assets/form-card-tiles/supervisor-injury-report.png',
            formAttachmentRequirements: {
                uploads: [{
                    identifier: 'picturesDiagrams',
                    label: 'Pictures/Diagrams',
                    required: 0
                }], 
                signatures: [
                    {
                        identifier: 'signature',
                        label: 'Signature',
                        required: true
                    }
                ]
            }
        };
    }

    static getFieldMappings(): Record<string, string> {
        return {
            submittedByName: "Submitter Name",
            submittedByEmail: "Submitter Email",
            submissionDate: "Submission Date",
 
            typeOfIncident: "Type of Incident",
            employeeFirstName: "Name (First)",
            employeeLastName: "Name (Last)",
            streetAddress: "Street Address",
            city: "City",
            stateProvince: "State / Province",
            zipPostalCode: "ZIP / Postal Code",
            phone: "Phone",
            dateOfHire: "Date of Hire",
            craftAndPosition: "Craft and Position",
            jobName: "Job Name",
            shiftWorked: "Shift Worked",
            supervisorFirstName: "Supervisor Name (First)",
            supervisorLastName: "Supervisor Name (Last)",
            hoursWorkedPerDay: "Hours Worked Per Day",
            hoursWorkedPerWeek: "Hours Worked Per Week",
            dateOfIncident: "Date of Incident",
            timeOfIncident: "Time of Incident",
            dateReported: "Date Reported",
            timeReported: "Time Reported",
            incidentLocation: "Exact Location of Incident",
            weather: "Weather",
            temperature: "Temperature (Fahrenheit)",
            equipment1: "Equipment/Tools Involved #1",
            equipment2: "Equipment/Tools Involved #2",
            anyDefects: "Any Defects",
            coveredInPreTask: "Was the Task/Job Covered on the Pre-Task?",
            witness1: "Witness Names/Contact Information (Complete Witness Report 8.04) #1",
            witness2: "Witness Names/Contact Information (Complete Witness Report 8.04) #2",
            employeeActivity: "What was the Employee Doing at the Time of the Incident? (Explain in Detail)",
            contributingFactors: "Could There Have Been Contributing Factors that Lead Up to the Incident?",
            safetyProcedures: "What Safety Procedures Were Involved in the Task?",
            employeeFamiliarWithProcedures: "Was the Employeed Familiar/Trained with the Procedures and Task?",
            employeeFollowedProcedures: "Did the Employee Follow the Procedures?",
            whyNotFollowed: "Why Not?",
            // Incident Causes
            incidentCausesLabel:"What Cause the Incident? (Check all that Apply)",
            awkwardBodyPositioning: "Awkward Body Positioning",
            causedByThirdParty: "Caused by a Third Party",
            complacency: "Complacency",
            incorrectTool: "Incorrect Tool for the Job",
            materialFailure: "Material Failure",
            equipmentToolFailure: "Equipment/Tool Failure",
            failureToMeetTargets: "Failure to Meet or Set Targets",
            failureToUsePpe: "Failure to Use Personal Protective Equipment",
            lackOfFallProtection: "Lack of Fall Protection",
            improperLiftingTechnique: "Improper Lifting Technique",
            improperLadderUsage: "Improper Ladder Usage",
            ignoringSafeWorkProcedures: "Ignoring Safe Work Procedures",
            lackOfKnowledgeTraining: "Lack of Knowledge/Training",
            notCommunicating: "Not Communicating",
            numerousMinorInjuries: "Numerous Minor Injuries",
            occupationalIllness: "Occupational Illness",
            physicalViolence: "Physical Violence",
            poorHousekeeping: "Poor Housekeeping",
            poorVisibilityLighting: "Poor Visibility/Lighting",
            preoccupied: "Preoccupied",
            slippingTripping: "Slipping/Tripping",
            supervisorManagementFailure: "Supervisor/Management Failure",
            unresolvedIssues: "Unresolved Issues",
            willfulRuleViolation: "Willful Rule Violation",
            otherCause: "Other",
            otherCauseSpecify: "Specify Other",
            incidentCausesDetail: "Explain in Detail All the Causes of the Incident",
            injured: "Were They Injured?",
            bloodExposure: "Were They Exposed to Another Person's Blood?",
            seekMedicalTreatment: "Will They Seek Medical Treatment?",
            treatmentFacilityName: "Name of Treatment Facility",
            doctorFirstName: "Name of Doctor (First)",
            doctorLastName: "Name of Doctor (Last)",
            doctorContactInfo: "Doctor Contact Information",
            propertyDamage: "Was There Property Damage?",
            othersInvolved: "Were Other Persons or Objects Involved?",
            preventionSuggestion: "How Could this Incident Have Been Avoided?",
            picturesDiagrams: "Pictures/Diagrams",
            signature: "Signature",
            signatureDate: "Date"
        };
    }

} 